import { KeyValue } from '../types/common.types';
import { ACTION_TYPES } from '../constants/actions';
import { FilterGroup } from '@common-modules/product-catalogue';
import { Dispatch } from 'redux';
import {
  filterProduct,
  getFilterData,
  getFilterDataWithStatus,
  getProductList,
  getSelectedFilters
} from '../helpers/productCatalogHelper';
import {
  ApplyFilterParams,
  FormattedProduct,
  GetFilterDataParams,
  GetProductListParams
} from '../types/productCatalogs.types';
import { CATALOGUE } from '../constants';
import { deepCopy } from '../helpers/common';
import { Plan } from '../types/plan.types';

export interface ProductCatalogState {
  filterData: FilterGroup[];
  productList: FormattedProduct[];
  originalProductList: FormattedProduct[];
  hasIppFilter: boolean;
  isDoneResetFilter?: boolean;
  backupFilterData: FilterGroup[];
  isLoading: boolean;
  tempSelectedPlan: Plan | null;
}

const initialState: ProductCatalogState = {
  filterData: [],
  productList: [],
  originalProductList: [],
  hasIppFilter: null,
  isDoneResetFilter: false,
  backupFilterData: [],
  isLoading: true,
  tempSelectedPlan: null
};

type ProductCatalogActions = {
  value?: KeyValue | string | boolean;
  type: string;
};

export const productCatalogReducer = (
  state: ProductCatalogState = initialState,
  action: ProductCatalogActions
): ProductCatalogState => {
  switch (action.type) {
    case ACTION_TYPES.PRODUCT_CATALOGS.SET_FILTER_DATA: {
      const filterData = getFilterDataWithStatus({
        filterData: getFilterData(action.value as GetFilterDataParams),
        products: state.productList,
        allProducts: state.originalProductList,
        selectedPlan: (action.value as GetFilterDataParams)?.selectedPlan
      });
      return {
        ...state,
        filterData,
        isDoneResetFilter: false,
        backupFilterData: deepCopy(filterData)
      };
    }

    case ACTION_TYPES.PRODUCT_CATALOGS.RESET_TO_PREVIOUS_FILTER: {
      return {
        ...state,
        filterData: deepCopy(state.backupFilterData),
        isDoneResetFilter: false
      };
    }

    case ACTION_TYPES.PRODUCT_CATALOGS.SELECT_FILTER: {
      const { filterData, selectedPlan } = action.value as ApplyFilterParams;
      const hasIpp = filterData.find(
        item => item.type === CATALOGUE.FILTER_TYPES.PLAN
      )?.items?.[0]?.selected;
      const tempSelectedPlan = hasIpp
        ? state.tempSelectedPlan || selectedPlan
        : selectedPlan;
      const filteredProduct = filterProduct({
        products: state.originalProductList,
        filterData,
        selectedPlan: tempSelectedPlan
      });
      const filterDataWithStatus = [
        ...getFilterDataWithStatus({
          filterData,
          products: filteredProduct,
          allProducts: state.originalProductList,
          selectedPlan: tempSelectedPlan
        })
      ];
      return {
        ...state,
        filterData: filterDataWithStatus,
        tempSelectedPlan: hasIpp ? state.tempSelectedPlan || selectedPlan : null
      };
    }

    case ACTION_TYPES.PRODUCT_CATALOGS.APPLY_FILTER: {
      const { filterData, selectedPlan } = action.value as ApplyFilterParams;
      const filteredProduct = filterProduct({
        products: state.originalProductList,
        filterData,
        selectedPlan: state.tempSelectedPlan || selectedPlan
      });
      const selectedFilters = getSelectedFilters(filterData);
      const filterDataWithStatus = [
        ...getFilterDataWithStatus({
          filterData,
          products: filteredProduct,
          allProducts: state.originalProductList,
          selectedPlan: state.tempSelectedPlan || selectedPlan
        })
      ];
      return {
        ...state,
        filterData: filterDataWithStatus,
        productList: filteredProduct,
        hasIppFilter: !!selectedFilters.planFilters?.length,
        isDoneResetFilter: false,
        backupFilterData: deepCopy(filterDataWithStatus),
        tempSelectedPlan: null
      };
    }
    case ACTION_TYPES.PRODUCT_CATALOGS.CLEAR_FILTER: {
      const filterData = state.filterData.map(filterGroup => {
        return {
          ...filterGroup,
          items: filterGroup.items.map(filterItem => ({
            ...filterItem,
            selected: false,
            disabled: false
          }))
        };
      });
      return {
        ...state,
        filterData,
        backupFilterData: deepCopy(filterData),
        isDoneResetFilter: true,
        hasIppFilter: false,
        tempSelectedPlan: null
      };
    }
    case ACTION_TYPES.PRODUCT_CATALOGS.SET_PRODUCT_LIST: {
      const productListPrams = action.value as GetProductListParams;
      if (state.hasIppFilter === false && productListPrams.queryParams) {
        delete productListPrams.queryParams[CATALOGUE.FILTER_TYPES.PLAN];
      }
      const products = getProductList(productListPrams);
      const filterData = getFilterDataWithStatus({
        filterData: state.filterData,
        products: products.filteredProduct,
        allProducts: products.allProduct,
        selectedPlan: (action.value as GetProductListParams)?.selectedPlan
      });
      return {
        ...state,
        productList: products.filteredProduct,
        originalProductList: products.allProduct,
        filterData,
        isLoading: false,
        isDoneResetFilter: false
      };
    }
    case ACTION_TYPES.PRODUCT_CATALOGS.SET_IPP_FILTER: {
      const filterData = state.filterData.map(filterGroup => {
        if (filterGroup.type === CATALOGUE.FILTER_TYPES.PLAN) {
          filterGroup.items[0].selected = action.value as boolean;
        }
        return filterGroup;
      });
      return {
        ...state,
        hasIppFilter: !!action.value as boolean,
        filterData
      };
    }
  }
  return state;
};

export const resetToPreviousFilter = () => (dispatch: Dispatch) => {
  dispatch({
    type: ACTION_TYPES.PRODUCT_CATALOGS.RESET_TO_PREVIOUS_FILTER
  });
};

export const selectFilter = (value: ApplyFilterParams) => (
  dispatch: Dispatch
) => {
  dispatch({
    type: ACTION_TYPES.PRODUCT_CATALOGS.SELECT_FILTER,
    value
  });
};

export const setFilterData = (value: GetFilterDataParams) => (
  dispatch: Dispatch
) => {
  dispatch({ type: ACTION_TYPES.PRODUCT_CATALOGS.SET_FILTER_DATA, value });
};

export const setProductList = (value: GetProductListParams) => (
  dispatch: Dispatch
) => {
  dispatch({ type: ACTION_TYPES.PRODUCT_CATALOGS.SET_PRODUCT_LIST, value });
};

export const applyFilter = (value: ApplyFilterParams) => (
  dispatch: Dispatch
) => {
  dispatch({
    type: ACTION_TYPES.PRODUCT_CATALOGS.APPLY_FILTER,
    value
  });
};

export const clearFilter = () => (dispatch: Dispatch) => {
  dispatch({
    type: ACTION_TYPES.PRODUCT_CATALOGS.CLEAR_FILTER
  });
};

export const setIppFilter = (value: boolean) => (dispatch: Dispatch) => {
  dispatch({ type: ACTION_TYPES.PRODUCT_CATALOGS.SET_IPP_FILTER, value });
};
